<script setup>
import { ref, onMounted, computed, onBeforeUnmount } from "vue";
import VideoPlayer from "@/components/VideoPlayer.vue";
import UpcomingEvents from "@/components/UpcomingEvents.vue";
import { Form, Field } from "vee-validate";
import { Fancybox } from "@fancyapps/ui";
import * as Yup from "yup";
import axios from "axios";
import { useTwitchStore } from "@/store/twitch-store";

import "@fancyapps/ui/dist/fancybox.css";

/* ==========================================================
   STATE
========================================================== */

const message = ref("");
const yourTitle = ref("");

const alertError = ref(false);
const alertMessage = ref("");
const alert = ref(false);
const loading = ref(false);

const nodeEnv = import.meta.env.MODE;
const twitchStore = useTwitchStore();
const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const getScheduleTime = (hour) => {
  const reference = new Date();

  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    year: "numeric",
    month: "numeric",
    day: "numeric",
  }).formatToParts(reference);

  const getPart = (type) => parts.find((part) => part.type === type)?.value;

  const year = Number(getPart("year"));
  const month = Number(getPart("month"));
  const day = Number(getPart("day"));

  // Temporary date representing the scheduled wall-clock time.
  const utcGuess = new Date(Date.UTC(year, month - 1, day, hour));

  const laOffsetParts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    timeZoneName: "longOffset",
  }).formatToParts(utcGuess);

  const offset =
    laOffsetParts.find((part) => part.type === "timeZoneName")?.value ??
    "GMT-08:00";

  const match = offset.match(/GMT([+-])(\d{2}):?(\d{2})?/);

  let offsetMinutes = 0;

  if (match) {
    const sign = match[1] === "+" ? 1 : -1;

    offsetMinutes = sign * (Number(match[2]) * 60 + Number(match[3] || 0));
  }

  return new Date(utcGuess.getTime() - offsetMinutes * 60000);
};

const localSchedule = computed(() => {
  const start = getScheduleTime(9);
  const end = getScheduleTime(12);

  const formatter = new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });

  return `Mon – Thu · ${formatter.format(start)} – ${formatter.format(end)}`;
});

const isTwitchLive = computed(() => {
  return twitchStore.isLive;
});

const twitchStream = computed(() => {
  return twitchStore.stream;
});

const twitchX = ref(0);
const twitchY = ref(0);

const twitchParallaxStyle = computed(() => ({
  transform: `translate(
            calc(-50% + ${twitchX.value}px),
            calc(-50% + ${twitchY.value}px)
        )`,
}));

const handleTwitchMouseMove = (event) => {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    return;
  }

  const rect = event.currentTarget.getBoundingClientRect();

  const x = event.clientX - rect.left - rect.width / 2;

  const y = event.clientY - rect.top - rect.height / 2;

  twitchX.value = x * 0.025;

  twitchY.value = y * 0.025;
};

const resetTwitchParallax = () => {
  twitchX.value = 0;
  twitchY.value = 0;
};

/* ==========================================================
   VALIDATION
========================================================== */

const schema = Yup.object().shape({
  name: Yup.string().required("Name is required").trim(),

  email: Yup.string()
    .required("Email is required")
    .email("Email is invalid")
    .trim(),

  message: Yup.string().required("Message is required").trim(),
});

/* ==========================================================
   VIDEO PLAYER
========================================================== */

const videoOptions = {
  autoplay: false,
  controls: true,
  fluid: true,
  preload: "metadata",
  eventTracking: true,
};

const playlist = [
  {
    sources: [
      {
        src:
          "https://d1wx9pshsk2zdp.cloudfront.net/" +
          "video-mixes/" +
          "DjSatuRnReggaetonCumbia20230116.mp4",

        type: "video/mp4",
      },
    ],

    description: "",

    poster: "../assets/images/djsaturn-02.jpg",

    thumbnail: [
      {
        srcset: "../assets/images/djsaturn-02.jpg",

        type: "image/png",

        media: "(max-width: 125px;)",
      },
      {
        src: "../assets/images/djsaturn-02.jpg",
      },
    ],

    name: "Reggaeton Cumbia 2023 - 01",

    duration: 1272,
  },

  {
    sources: [
      {
        src:
          "https://d1wx9pshsk2zdp.cloudfront.net/" +
          "video-mixes/" +
          "HouseMix20221228.mp4",

        type: "video/mp4",
      },
    ],

    description: "",

    poster: "../assets/images/djsaturn-02.jpg",

    thumbnail: [
      {
        srcset: "../assets/images/djsaturn-albumart.png",

        type: "image/png",

        media: "(max-width: 125px;)",
      },
      {
        src: "../assets/images/djsaturn-albumart.png",
      },
    ],

    name: "House Mix 2022 - 12",

    duration: 1419,
  },

  {
    sources: [
      {
        src:
          "https://d1wx9pshsk2zdp.cloudfront.net/" +
          "video-mixes/" +
          "LatinMix20221127.mp4",

        type: "video/mp4",
      },
    ],

    description: "",

    poster: "../assets/images/djsaturn-02.jpg",

    thumbnail: [
      {
        srcset: "../assets/images/djsaturn-02.jpg",

        type: "image/png",

        media: "(max-width: 125px;)",
      },
      {
        src: "../assets/images/djsaturn-02.jpg",
      },
    ],

    name: "Latin Mix 2022 - 11",

    duration: 1505,
  },

  {
    sources: [
      {
        src:
          "https://d1wx9pshsk2zdp.cloudfront.net/" +
          "video-mixes/" +
          "DanceMix20221001.mp4",

        type: "video/mp4",
      },
    ],

    description: "",

    poster: "../assets/images/djsaturn-02.jpg",

    thumbnail: [
      {
        srcset: "../assets/images/djsaturn-albumart.png",

        type: "image/png",

        media: "(max-width: 125px;)",
      },
      {
        src: "../assets/images/djsaturn-albumart.png",
      },
    ],

    name: "Dance Mix 2022-10",

    duration: 1314,
  },

  {
    sources: [
      {
        src:
          "https://d1wx9pshsk2zdp.cloudfront.net/" +
          "video-mixes/" +
          "DjSatuRn_Demo_Mix0509.mp4",

        type: "video/mp4",
      },
    ],

    thumbnail: [
      {
        srcset: "../assets/images/djsaturn-02.jpg",

        type: "image/jpg",

        media: "(max-width: 125px;)",
      },
      {
        src: "../assets/images/djsaturn-02.jpg",
      },
    ],

    name: "Various Mix 2009 - 05",

    duration: 1355,
  },
];

const initTwitchPlayer = () => {
  const element = document.getElementById("twitch-embed");

  if (!element || !window.Twitch || element.querySelector("iframe")) {
    return;
  }

  new window.Twitch.Embed("twitch-embed", {
    width: "100%",
    height: 600,
    channel: "djsaturn_",
  });
};

/* ==========================================================
   BOOKING FORM
========================================================== */

const onSubmit = async (values, { resetForm }) => {
  alertError.value = false;
  alert.value = false;
  alertMessage.value = "";
  loading.value = true;

  const form = new FormData();

  const phone = values.phone || "";

  form.append("name", values.name);

  form.append("email", values.email);

  form.append("yourTitle", yourTitle.value);

  form.append("phone", phone);

  form.append("message", values.message);

  try {
    const response = await axios.post(
      process.env.VUE_APP_API_URL + "sendemail",

      form
    );

    alert.value = true;

    alertMessage.value = response.data.message;

    if (nodeEnv === "production" && window._paq) {
      window._paq.push(["trackEvent", "Booking", "Inquiry submitted"]);
    }

    resetForm();
  } catch (error) {
    alert.value = true;
    alertError.value = true;

    alertMessage.value =
      error.response?.data?.message ||
      "Unable to send your inquiry. Please try again.";
  } finally {
    loading.value = false;
  }
};

/* ==========================================================
   ANALYTICS
========================================================== */

const trackEvent = (event) => {
  if (nodeEnv === "production" && window._paq) {
    window._paq.push(["trackEvent", "link", event]);
  }

  return true;
};

/* ==========================================================
   MOUNT
========================================================== */

onMounted(async () => {
  /*
   * Twitch player
   */
  try {
    await loadTwitchEmbed();
    initTwitchPlayer();
  } catch (error) {
    console.error("Unable to load Twitch embed:", error);
  }

  /*
   * Fancybox
   */
  Fancybox.bind("[data-fancybox]", {});

  twitchStore.checkLive();
});

/* ==========================================================
   CLEANUP
========================================================== */

onBeforeUnmount(() => {
  Fancybox.unbind("[data-fancybox]");
});

const loadTwitchEmbed = () => {
  return new Promise((resolve, reject) => {
    // Twitch is already available
    if (window.Twitch) {
      resolve();
      return;
    }

    // Script may already be loading
    const existingScript = document.querySelector(
      'script[src="https://embed.twitch.tv/embed/v1.js"]'
    );

    if (existingScript) {
      existingScript.addEventListener("load", resolve);
      existingScript.addEventListener("error", reject);
      return;
    }

    const script = document.createElement("script");

    script.src = "https://embed.twitch.tv/embed/v1.js";
    script.async = true;

    script.onload = resolve;
    script.onerror = reject;

    document.head.appendChild(script);
  });
};
</script>

<template>
  <main class="dj-home">
    <!-- ==================================================
             HERO
        =================================================== -->
    <section class="hero-section">
      <div class="hero-overlay"></div>

      <div class="container position-relative">
        <div class="row hero-padding align-items-center">
          <!-- HERO CONTENT -->
          <div class="col-lg-7 col-xl-7 hero-content">
            <div class="hero-eyebrow" data-aos="fade-up">
              <span></span>
              Los Angeles • Open Format DJ
            </div>

            <h1 class="hero-title" data-aos="fade-up" data-aos-delay="100">
              DJ SATURN
            </h1>
            <div class="hero-specialty" data-aos="fade-up" data-aos-delay="200">
              Music Video DJ
            </div>

            <p class="hero-lead" data-aos="fade-up" data-aos-delay="300">
              Music. Energy. Community.
            </p>

            <p class="hero-description" data-aos="fade-up" data-aos-delay="400">
              Open-format DJ bringing high-energy sets to nightclubs, private
              events and live streams.
            </p>

            <div class="hero-actions" data-aos="fade-up" data-aos-delay="500">
              <a
                href="#contact-desktop"
                class="btn btn-outline-light btn-lg d-none d-lg-inline"
              >
                Book DJ Saturn
              </a>
              <a
                href="#contact-mobile"
                class="btn btn-outline-light btn-lg d-lg-none"
              >
                Book DJ Saturn
              </a>
            </div>

            <div class="hero-social" data-aos="fade-up" data-aos-delay="600">
              <a
                href="https://www.twitch.tv/djsaturn_"
                target="_blank"
                rel="noopener"
                aria-label="DJ Saturn on Twitch"
              >
                <i class="bi bi-twitch"></i>
              </a>

              <a
                href="https://www.instagram.com/djs4turn"
                target="_blank"
                rel="noopener"
                aria-label="DJ Saturn on Instagram"
              >
                <i class="bi bi-instagram"></i>
              </a>

              <a
                href="https://www.tiktok.com/@djsaturn_"
                target="_blank"
                rel="noopener"
                aria-label="DJ Saturn on TikTok"
              >
                <i class="bi bi-tiktok"></i>
              </a>
            </div>
          </div>

          <!-- DJ SATURN IMAGE -->
          <div
            class="d-none d-lg-block col-lg-5 col-xl-5 hero-image-col"
            data-aos="fade-left"
            data-aos-delay="200"
          >
            <div class="hero-image-wrapper">
              <div class="hero-image-accent"></div>

              <img
                class="hero-dj-image img-fluid"
                src="assets/images/djsaturnmix.jpg"
                alt="DJ Saturn performing"
              />

              <div class="hero-image-label">
                <span>Los Angeles</span>
                <strong>DJ SATURN</strong>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ==================================================
             TWITCH CTA
        =================================================== -->
    <section
      id="twitch"
      class="twitch-cta"
      :class="{ 'is-live': isTwitchLive }"
      @mousemove="handleTwitchMouseMove"
      @mouseleave="resetTwitchParallax"
    >
      <!-- ATMOSPHERE -->

      <div class="twitch-ambient" aria-hidden="true">
        <div class="twitch-glow twitch-glow-1"></div>
        <div class="twitch-glow twitch-glow-2"></div>

        <div class="twitch-beam twitch-beam-1"></div>
        <div class="twitch-beam twitch-beam-2"></div>

        <div class="twitch-grid"></div>
      </div>

      <!-- LARGE BACKGROUND TYPE -->

      <div
        class="twitch-background-text"
        :style="twitchParallaxStyle"
        aria-hidden="true"
      >
        {{ isTwitchLive ? "LIVE" : "TWITCH" }}
      </div>

      <div class="container position-relative">
        <div class="row justify-content-center">
          <div class="col-lg-9 col-xl-8 text-center" data-aos="fade-up">
            <!-- STATUS -->

            <div class="section-eyebrow justify-content-center">
              <span></span>

              <template v-if="isTwitchLive">
                <span class="live-dot"></span>
                Live on Twitch
              </template>

              <template v-else> DJ Saturn on Twitch </template>

              <span></span>
            </div>

            <!-- HEADLINE -->

            <h2 class="display-title">
              <template v-if="isTwitchLive">
                The Party
                <span>Continues Live.</span>
              </template>

              <template v-else>
                Catch The
                <span>Next Stream.</span>
              </template>
            </h2>

            <!-- LIVE INFO -->

            <div v-if="isTwitchLive && twitchStream" class="twitch-stream-info">
              <div class="live-now-badge">
                <span class="live-dot"></span>
                Live Now
              </div>

              <h3 class="twitch-stream-title">
                {{ twitchStream.title }}
              </h3>

              <div class="twitch-stream-meta">
                <span v-if="twitchStream.game">
                  <i class="bi bi-music-note-beamed"></i>

                  {{ twitchStream.game }}
                </span>

                <span v-if="twitchStream.viewers !== undefined">
                  <i class="bi bi-eye-fill"></i>

                  {{ twitchStream.viewers.toLocaleString() }}

                  watching
                </span>
              </div>
            </div>

            <!-- OFFLINE -->

            <p v-else class="section-description mx-auto mb-0">
              Follow DJ Saturn on Twitch for open-format DJ sets, song requests,
              dance parties and good vibes with the community.
            </p>
            <!-- STREAM SCHEDULE -->
            <div v-if="!isTwitchLive" class="twitch-schedule">
              <div class="twitch-schedule-label">
                <i class="bi bi-calendar-week me-2"></i>
                Stream Schedule
              </div>

              <div class="twitch-schedule-time">
                {{ localSchedule }}
              </div>

              <div class="twitch-schedule-zone">
                <i class="bi bi-globe2 me-1"></i>
                Your local time · {{ localTimeZone }}
              </div>
            </div>

            <!-- FEATURES -->

            <div class="twitch-features">
              <span>
                <i class="bi bi-music-note-beamed"></i>
                Live DJ Sets
              </span>

              <span>
                <i class="bi bi-chat-heart"></i>
                Requests
              </span>

              <span>
                <i class="bi bi-people"></i>
                Community
              </span>
            </div>

            <!-- CTA -->

            <div class="twitch-button-wrap">
              <div v-if="isTwitchLive" class="twitch-button-glow"></div>

              <a
                href="https://www.twitch.tv/djsaturn_"
                target="_blank"
                rel="noopener"
                class="btn btn-twitch btn-lg"
                @click="trackEvent('Twitch-Center-Button')"
              >
                <template v-if="isTwitchLive">
                  <span class="live-dot me-2"></span>
                  Watch Live
                </template>

                <template v-else>
                  <i class="bi bi-twitch me-2"></i>
                  Follow on Twitch
                </template>
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- BOTTOM FADE -->

      <div class="twitch-bottom-fade" aria-hidden="true"></div>
    </section>
    <!-- ==================================================
             TWITCH PLAYER
        =================================================== -->
    <section id="watch-live" class="stream-section">
      <div class="container">
        <!-- TWITCH PLAYER -->

        <div class="stream-wrapper" data-aos="fade-up" data-aos-delay="100">
          <!-- PLAYER BAR -->

          <div class="stream-topbar">
            <div class="d-flex align-items-center gap-2">
              <template v-if="isTwitchLive">
                <span class="live-dot"></span>

                <strong> LIVE </strong>
              </template>

              <template v-else>
                <i class="bi bi-twitch"></i>

                <strong> DJ SATURN </strong>
              </template>
            </div>

            <a
              href="https://www.twitch.tv/djsaturn_"
              target="_blank"
              rel="noopener"
              @click="trackEvent('Twitch-Player-Link')"
            >
              Open on Twitch

              <i class="bi bi-box-arrow-up-right ms-1"></i>
            </a>
          </div>

          <!-- EMBED -->

          <div id="twitch-embed"></div>
        </div>
      </div>
    </section>
    <!-- <UpcomingEvents /> -->

    <!-- ==================================================
             DEMO MIXES
        =================================================== -->
    <section id="videoMixes" class="mixes-section">
      <div class="container">
        <div class="row align-items-end mb-5" data-aos="fade-up">
          <div class="col-lg-7">
            <div class="section-eyebrow">
              <span></span>
              Demo Mixes
            </div>

            <h2 class="display-title mb-0">
              Listen.
              <span>Watch. Dance.</span>
            </h2>
          </div>

          <div class="col-lg-5 mt-3 mt-lg-0">
            <p class="section-description mb-0">
              A selection of live video mixes showcasing DJ Saturn's open-format
              sound.
            </p>
          </div>
        </div>

        <div class="mix-player" data-aos="fade-up" data-aos-delay="100">
          <video-player :options="videoOptions" :playlist="playlist" />
        </div>
      </div>
    </section>

    <!-- ==================================================
             ABOUT + BOOKING
        =================================================== -->
    <section class="about-section" id="contact-desktop">
      <div class="container">
        <div class="row g-5 g-xl-6 align-items-start">
          <!-- ABOUT -->
          <!-- ABOUT -->
          <div class="col-lg-5" data-aos="fade-up">
            <div class="section-eyebrow">
              <span></span>
              About DJ Saturn
            </div>

            <h2 class="display-title">
              More Than
              <span>Just Music.</span>
            </h2>

            <p class="about-lead">
              Professional open-format DJ with more than 20 years of experience
              bringing energy to nightclubs, private events, virtual events and
              live audiences.
            </p>

            <p class="section-description">
              DJ Saturn specializes in Top 40, Hip Hop, Dance, Electro, Rock En
              Español, 80s, Guaracha, Reggaeton, Cumbia, Merengue and Salsa.
            </p>

            <p class="section-description">
              After spending approximately 12 years as a resident DJ at The
              Mayan Nightclub in Los Angeles, Saturn developed an instinct for
              programming the room, building energy and keeping the dance floor
              moving throughout the night.
            </p>

            <p class="section-description">
              With over two years of live streaming experience on Twitch, DJ
              Saturn has brought that same nightclub energy into the virtual
              world. From interactive live DJ sets and audience-driven
              experiences to virtual events on Utherverse, each experience
              blends music, visuals and live interaction to make the audience
              feel like they're part of the party — not just watching it.
            </p>

            <div class="about-stats">
              <div>
                <strong>20+</strong>
                <span>Years Experience</span>
              </div>

              <div>
                <strong>15+</strong>
                <span>Years Nightclub DJ Experience</span>
              </div>

              <div>
                <strong>2+</strong>
                <span>Years Streaming Experience</span>
              </div>

              <div>
                <strong>1,500+</strong>
                <span>Person Audiences</span>
              </div>
            </div>
          </div>

          <!-- BOOKING FORM -->
          <div
            class="col-lg-6 offset-lg-1"
            data-aos="fade-up"
            data-aos-delay="150"
            id="contact-mobile"
          >
            <div class="booking-card">
              <div class="booking-heading">
                <div class="section-eyebrow">
                  <span></span>
                  Booking
                </div>

                <h3>
                  Let's Work
                  <span>Together.</span>
                </h3>

                <p>Tell me about your event and I'll get back to you.</p>
              </div>

              <div
                v-if="alert"
                class="alert"
                :class="alertError ? 'alert-danger' : 'alert-success'"
                role="alert"
                v-html="alertMessage"
              ></div>

              <Form
                @submit="onSubmit"
                :validation-schema="schema"
                v-slot="{ errors }"
              >
                <!-- NAME -->
                <div
                  class="form-floating mb-3"
                  :class="{
                    'is-invalid': errors.name,
                  }"
                >
                  <Field
                    type="text"
                    class="form-control"
                    name="name"
                    id="name"
                    placeholder="Name"
                    :class="{
                      'is-invalid': errors.name,
                    }"
                  />
                  <label for="name"> Name </label>
                  <div class="invalid-feedback">
                    {{ errors.name }}
                  </div>
                </div>

                <!-- EMAIL + PHONE -->
                <div class="row">
                  <div class="col-md-6">
                    <div class="form-floating mb-3">
                      <Field
                        type="email"
                        class="form-control"
                        name="email"
                        id="email"
                        placeholder="Email"
                        :class="{
                          'is-invalid': errors.email,
                        }"
                      />

                      <label for="email"> Email Address </label>

                      <div class="invalid-feedback">
                        {{ errors.email }}
                      </div>
                    </div>
                  </div>

                  <div class="col-md-6">
                    <div class="form-floating mb-3">
                      <Field
                        type="text"
                        class="form-control"
                        name="phone"
                        id="phone"
                        placeholder="Phone"
                      />

                      <label for="phone">
                        Phone
                        <span class="text-muted"> (Optional) </span>
                      </label>
                    </div>
                  </div>
                </div>

                <!-- MESSAGE -->
                <div class="form-floating mb-3">
                  <Field
                    name="message"
                    v-slot="{ handleChange, handleBlur, field }"
                  >
                    <textarea
                      v-bind="field"
                      class="form-control"
                      id="message"
                      placeholder="Message"
                      :class="{
                        'is-invalid': errors.message,
                      }"
                      @change="handleChange"
                      @blur="handleBlur"
                    ></textarea>
                  </Field>

                  <label for="message"> Tell me about your event </label>

                  <div class="invalid-feedback">
                    {{ errors.message }}
                  </div>
                </div>

                <!-- HONEYPOT -->
                <div class="d-none">
                  <label for="yourTitle"> Title </label>

                  <input
                    id="yourTitle"
                    type="text"
                    name="yourTitle"
                    v-model="yourTitle"
                  />
                </div>

                <!-- SUBMIT -->
                <div class="d-grid">
                  <button
                    v-if="!loading"
                    type="submit"
                    class="btn btn-primary btn-lg"
                  >
                    Send Booking Inquiry
                    <i class="bi bi-arrow-right ms-2"></i>
                  </button>

                  <button
                    v-else
                    class="btn btn-primary btn-lg"
                    type="button"
                    disabled
                  >
                    <span class="spinner-border spinner-border-sm me-2"></span>

                    Sending...
                  </button>
                </div>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<style scoped>
/* ==========================================================
   GLOBAL
========================================================== */

.dj-home {
  position: relative;
  width: 100%;
  max-width: 100%;
  overflow-x: clip;

  background: var(--dj-bg);
  color: #fff;
}
section {
  position: relative;
}

.section-eyebrow,
.hero-eyebrow {
  display: flex;
  align-items: center;
  gap: 0.75rem;

  margin-bottom: 1.25rem;

  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.18rem;
  text-transform: uppercase;
}

.section-eyebrow span,
.hero-eyebrow span {
  display: block;
  width: 32px;
  height: 2px;
  background: var(--bs-primary);
}

.display-title {
  color: #fff;

  font-size: clamp(2.5rem, 5vw, 5rem);
  font-weight: 800;
  line-height: 0.95;

  letter-spacing: -0.04em;
  text-transform: uppercase;
}

.display-title span {
  display: block;
  color: var(--bs-primary);
}

.section-description {
  max-width: 600px;

  color: var(--dj-muted);

  font-size: 1.05rem;
  line-height: 1.8;
}

/* ==========================================================
   HERO
========================================================== */

.hero-section {
  position: relative;
  min-height: calc(100vh - 70px);
  padding-top: 5rem;
  overflow: hidden;
  background: url("../assets/lightingbg.jpg") center center / cover no-repeat;
}

.hero-section::after {
  content: "";

  position: absolute;
  inset: 0;

  background: linear-gradient(
    90deg,
    rgba(0, 0, 0, 0.94) 0%,
    rgba(0, 0, 0, 0.78) 45%,
    rgba(0, 0, 0, 0.25) 100%
  );
}

.hero-overlay {
  position: absolute;
  inset: 0;

  z-index: 1;

  background: linear-gradient(0deg, #090909 0%, transparent 25%);
}

.hero-section .container {
  z-index: 2;
}

.hero-title {
  margin-bottom: 0;

  color: #fff;

  font-size: clamp(5rem, 12vw, 10rem);
  font-weight: 800;
  line-height: 0.75;

  letter-spacing: -0.07em;
}

.hero-specialty {
  margin-top: 0.25rem;
  margin-bottom: 1rem;

  color: transparent;

  font-size: clamp(1.15rem, 2.2vw, 1.75rem);
  font-weight: 800;

  letter-spacing: 0.1em;
  text-transform: uppercase;

  -webkit-text-stroke: 1px rgba(255, 255, 255, 0.75);
}

.hero-lead {
  margin: 2rem 0 0.75rem;

  font-size: clamp(1.4rem, 2.5vw, 2rem);
  font-weight: 600;
}

.hero-description {
  max-width: 620px;
  margin-bottom: 1.5rem;
  color: rgba(255, 255, 255, 0.7);

  font-size: 1.1rem;
  line-height: 1.8;
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 1.5rem;
}

.hero-actions .btn {
  padding: 0.9rem 1.75rem;

  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04rem;
}

.hero-social {
  display: flex;
  gap: 1.5rem;

  margin-top: 1.5rem;
}

.hero-social a {
  color: rgba(255, 255, 255, 0.7);

  font-size: 1.35rem;

  transition: color 0.2s ease, transform 0.2s ease;
}

.hero-social a:hover {
  color: #fff;
  transform: translateY(-3px);
}

.hero-content {
  position: relative;
  z-index: 3;
}

.hero-image-col {
  position: relative;
  z-index: 2;
}

.hero-image-wrapper {
  position: relative;

  max-width: 520px;
  margin-left: auto;
}

.hero-dj-image {
  position: relative;
  z-index: 2;

  width: 100%;

  border-radius: 1.25rem;

  object-fit: cover;

  box-shadow: 0 40px 100px rgba(0, 0, 0, 0.55);
}

/* Accent block behind image */

.hero-image-accent {
  position: absolute;

  top: -25px;
  right: -25px;

  z-index: 1;

  width: 70%;
  height: 70%;

  border: 1px solid rgba(var(--bs-primary-rgb), 0.6);

  border-radius: 1.25rem;
}

/* Bottom image label */

.hero-image-label {
  position: absolute;

  right: -20px;
  bottom: -25px;

  z-index: 3;

  min-width: 190px;

  padding: 1rem 1.5rem;

  background: var(--bs-primary);

  color: #fff;
}

.hero-image-label span {
  display: block;

  margin-bottom: 0.15rem;

  font-size: 0.65rem;
  font-weight: 600;

  letter-spacing: 0.15rem;
  text-transform: uppercase;

  opacity: 0.75;
}

.hero-image-label strong {
  display: block;

  font-size: 1.15rem;
  font-weight: 800;

  letter-spacing: 0.05rem;
}

/* ==========================================================
   EYEBROW
========================================================== */

.twitch-cta .section-eyebrow {
  display: flex;
  align-items: center;

  gap: 0.75rem;

  margin-bottom: 1.5rem;

  color: rgba(255, 255, 255, 0.7);

  font-size: 0.75rem;
  font-weight: 700;

  letter-spacing: 0.15rem;

  text-transform: uppercase;
}

/* Decorative lines */

.twitch-cta .section-eyebrow > span:not(.live-dot) {
  display: inline-block;

  width: 35px;
  height: 1px;

  background: rgba(255, 255, 255, 0.35);
}

/* ==========================================================
   TITLE
========================================================== */

.twitch-cta .display-title {
  margin-bottom: 1.75rem;

  color: #fff;

  font-size: clamp(3rem, 7vw, 6rem);

  font-weight: 900;
  line-height: 0.9;

  letter-spacing: -0.055em;

  text-transform: uppercase;
}

.twitch-cta .display-title span {
  display: block;

  color: var(--twitch, #9146ff);
}

/* ==========================================================
   OFFLINE DESCRIPTION
========================================================== */

.twitch-cta .section-description {
  max-width: 650px;

  margin-bottom: 2rem;

  color: rgba(255, 255, 255, 0.65);

  font-size: 1.05rem;
  line-height: 1.8;
}

/* ==========================================================
   LIVE STREAM INFORMATION
========================================================== */

.twitch-stream-info {
  margin: 0 auto 2rem;
}

.twitch-stream-title {
  margin-bottom: 0;
  color: #fff;
  font-size: clamp(1.25rem, 2.5vw, 1.75rem);
  font-weight: 700;
  line-height: 1.4;
}

/* ==========================================================
   STREAM META
========================================================== */

.twitch-stream-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 0.75rem 1.5rem;
  margin-top: 0.85rem;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;
}

.twitch-stream-meta span {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

.twitch-stream-meta i {
  color: var(--twitch, #9146ff);
}

/* ==========================================================
   FEATURES
========================================================== */

.twitch-features {
  display: flex;
  flex-wrap: wrap;

  align-items: center;
  justify-content: center;

  gap: 0.75rem;

  margin: 2rem 0 2.5rem;
}

.twitch-features span {
  display: inline-flex;
  align-items: center;

  gap: 0.5rem;

  padding: 0.55rem 0.9rem;

  border: 1px solid rgba(255, 255, 255, 0.12);

  border-radius: 50rem;

  background: rgba(255, 255, 255, 0.04);

  color: rgba(255, 255, 255, 0.75);

  font-size: 0.8rem;
  font-weight: 600;

  backdrop-filter: blur(5px);
}

.twitch-features i {
  color: var(--twitch, #9146ff);

  font-size: 0.95rem;
}

/* ==========================================================
   TWITCH BUTTON
========================================================== */

.btn-twitch {
  display: inline-flex;
  align-items: center;
  justify-content: center;

  min-width: 210px;

  padding: 0.9rem 1.75rem;

  border: 1px solid var(--twitch, #9146ff);

  background: var(--twitch, #9146ff);

  color: #fff;

  font-size: 0.9rem;
  font-weight: 700;

  letter-spacing: 0.04rem;

  text-transform: uppercase;

  box-shadow: 0 12px 35px rgba(145, 70, 255, 0.25);

  transition: transform 0.2s ease, box-shadow 0.2s ease,
    background-color 0.2s ease;
}

.btn-twitch:hover,
.btn-twitch:focus {
  border-color: #a970ff;

  background: #a970ff;

  color: #fff;

  transform: translateY(-2px);

  box-shadow: 0 18px 45px rgba(145, 70, 255, 0.4);
}

/* ==========================================================
   LIVE DOT
========================================================== */

.live-dot {
  display: inline-block;

  width: 8px;
  height: 8px;

  flex: 0 0 8px;

  border-radius: 50%;

  background: #ff3b3b;

  box-shadow: 0 0 0 4px rgba(255, 59, 59, 0.15);

  animation: twitch-live-pulse 1.8s infinite;
}

@keyframes twitch-live-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 4px rgba(255, 59, 59, 0.15);
  }

  50% {
    box-shadow: 0 0 0 9px rgba(255, 59, 59, 0);
  }
}

/* ==========================================================
   IMMERSIVE TWITCH CTA
========================================================== */
.twitch-cta {
  position: relative;
  z-index: 1;

  display: flex;
  align-items: center;

  min-height: 720px;
  padding: 8rem 0;

  overflow: hidden;
  isolation: isolate;

  background: radial-gradient(
      circle at 50% 50%,
      rgba(145, 70, 255, 0.13),
      transparent 42%
    ),
    linear-gradient(180deg, #090909 0%, #0c0712 50%, #090909 100%);

  color: #fff;
}

/* ==========================================================
   AMBIENT LAYER
========================================================== */

.twitch-ambient {
  position: absolute;
  inset: 0;

  z-index: 0;

  overflow: hidden;

  pointer-events: none;
}

/* ==========================================================
   GLOWS
========================================================== */

.twitch-glow {
  position: absolute;

  border-radius: 50%;

  filter: blur(90px);

  opacity: 0.25;

  will-change: transform;
}

.twitch-glow-1 {
  top: -20%;
  left: -10%;

  width: 600px;
  height: 600px;

  background: rgba(145, 70, 255, 0.55);

  animation: twitchGlowOne 12s ease-in-out infinite alternate;
}

.twitch-glow-2 {
  right: -15%;
  bottom: -30%;

  width: 700px;
  height: 700px;

  background: rgba(90, 35, 180, 0.45);

  animation: twitchGlowTwo 15s ease-in-out infinite alternate;
}

@keyframes twitchGlowOne {
  from {
    transform: translate3d(0, 0, 0) scale(1);
  }

  to {
    transform: translate3d(120px, 80px, 0) scale(1.15);
  }
}

@keyframes twitchGlowTwo {
  from {
    transform: translate3d(0, 0, 0) scale(1);
  }

  to {
    transform: translate3d(-120px, -70px, 0) scale(1.2);
  }
}

/* ==========================================================
   LIGHT BEAMS
========================================================== */

.twitch-beam {
  position: absolute;

  top: -30%;

  width: 180px;
  height: 160%;

  background: linear-gradient(
    180deg,
    transparent,
    rgba(145, 70, 255, 0.09),
    transparent
  );

  filter: blur(10px);

  opacity: 0.6;

  transform-origin: top center;
}

.twitch-beam-1 {
  left: 18%;

  transform: rotate(24deg);

  animation: beamMoveOne 10s ease-in-out infinite alternate;
}

.twitch-beam-2 {
  right: 18%;

  transform: rotate(-24deg);

  animation: beamMoveTwo 12s ease-in-out infinite alternate;
}

@keyframes beamMoveOne {
  from {
    transform: translateX(-60px) rotate(18deg);
  }

  to {
    transform: translateX(90px) rotate(30deg);
  }
}

@keyframes beamMoveTwo {
  from {
    transform: translateX(70px) rotate(-18deg);
  }

  to {
    transform: translateX(-90px) rotate(-30deg);
  }
}

/* ==========================================================
   GRID / TEXTURE
========================================================== */

.twitch-grid {
  position: absolute;
  inset: 0;

  opacity: 0.12;

  background-image: linear-gradient(
      rgba(255, 255, 255, 0.05) 1px,
      transparent 1px
    ),
    linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px);

  background-size: 60px 60px;

  mask-image: radial-gradient(circle at center, #000 0%, transparent 70%);
}

/* ==========================================================
   BACKGROUND WORD
========================================================== */

.twitch-background-text {
  position: absolute;

  top: 50%;
  left: 50%;

  z-index: 1;

  transform: translate(-50%, -50%);

  color: rgba(255, 255, 255, 0.025);

  font-size: clamp(12rem, 30vw, 32rem);

  font-weight: 900;
  line-height: 0.75;

  letter-spacing: -0.09em;
  text-transform: uppercase;

  white-space: nowrap;

  pointer-events: none;
  user-select: none;

  transition: transform 0.15s ease-out;

  will-change: transform;
}

/* ==========================================================
   CONTENT
========================================================== */

.twitch-cta .container {
  position: relative;
  z-index: 3;
}

/* ==========================================================
   LIVE MODE
========================================================== */

.twitch-cta.is-live {
  background: radial-gradient(
      circle at 50% 50%,
      rgba(145, 70, 255, 0.23),
      transparent 42%
    ),
    linear-gradient(180deg, #090909 0%, #100719 50%, #090909 100%);
}

.twitch-cta.is-live .twitch-background-text {
  color: rgba(145, 70, 255, 0.07);

  text-shadow: 0 0 80px rgba(145, 70, 255, 0.15);
}

/* ==========================================================
   LIVE BADGE
========================================================== */

.live-now-badge {
  display: inline-flex;
  align-items: center;

  gap: 0.55rem;

  margin-bottom: 1rem;
  padding: 0.45rem 0.85rem;

  border: 1px solid rgba(255, 255, 255, 0.12);

  border-radius: 50rem;

  background: rgba(0, 0, 0, 0.35);

  color: rgba(255, 255, 255, 0.85);

  font-size: 0.7rem;
  font-weight: 800;

  letter-spacing: 0.1rem;
  text-transform: uppercase;

  backdrop-filter: blur(10px);
}

/* ==========================================================
   CTA GLOW
========================================================== */

.twitch-button-wrap {
  position: relative;

  display: inline-block;
}

.twitch-button-glow {
  position: absolute;

  inset: -20px;

  z-index: -1;

  border-radius: 50%;

  background: rgba(145, 70, 255, 0.45);

  filter: blur(30px);

  animation: twitchButtonGlow 2.5s ease-in-out infinite;
}

@keyframes twitchButtonGlow {
  0%,
  100% {
    opacity: 0.35;

    transform: scale(0.9);
  }

  50% {
    opacity: 0.7;

    transform: scale(1.1);
  }
}

/* ==========================================================
   BOTTOM FADE
========================================================== */

.twitch-bottom-fade {
  position: absolute;

  right: 0;
  bottom: 0;
  left: 0;

  z-index: 2;

  height: 140px;

  background: linear-gradient(to bottom, transparent, #090909);

  pointer-events: none;
}

/* ==========================================================
   MOBILE
========================================================== */

@media (max-width: 767.98px) {
  .twitch-cta {
    min-height: auto;
    padding: 6rem 0;
  }

  .twitch-beam {
    display: none;
  }

  .twitch-glow {
    filter: blur(70px);
  }

  .twitch-background-text {
    font-size: 45vw;
  }

  .twitch-cta .display-title {
    font-size: clamp(2.75rem, 14vw, 4rem);
  }

  .twitch-cta .section-description {
    font-size: 0.95rem;
  }

  .twitch-features {
    gap: 0.5rem;

    margin: 1.75rem 0 2rem;
  }

  .twitch-features span {
    padding: 0.45rem 0.7rem;

    font-size: 0.72rem;
  }

  .btn-twitch {
    width: 100%;
    max-width: 300px;
  }
}
/* ==========================================================
   STREAM
========================================================== */

.stream-section {
  padding: 7rem 0;

  background: #090909;
}

.stream-wrapper {
  overflow: hidden;

  border: 1px solid var(--dj-border);
  border-radius: 1rem;

  background: #050505;

  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.4);
}

.stream-topbar {
  display: flex;
  justify-content: space-between;
  align-items: center;

  padding: 1rem 1.25rem;

  border-bottom: 1px solid var(--dj-border);

  background: #111;
}

.stream-topbar a {
  color: var(--dj-muted);

  font-size: 0.85rem;
  text-decoration: none;
}

.stream-topbar a:hover {
  color: #fff;
}

#twitch-embed {
  width: 100%;
  min-height: 480px;
}

/* ==========================================================
   MIXES
========================================================== */

.mixes-section {
  padding: 8rem 0;

  border-top: 1px solid var(--dj-border);

  background: var(--dj-bg-soft);
}

.mix-player {
  padding: 1rem;

  border: 1px solid var(--dj-border);
  border-radius: 1rem;

  background: #090909;
}

/* ==========================================================
   ABOUT
========================================================== */

.about-section {
  padding: 8rem 0;

  border-top: 1px solid var(--dj-border);

  background: radial-gradient(
      circle at 10% 50%,
      rgba(var(--bs-primary-rgb), 0.08),
      transparent 35%
    ),
    #090909;
}

.about-lead {
  margin-top: 2rem;

  color: #fff;

  font-size: 1.3rem;
  line-height: 1.7;
}

.about-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-top: 2rem;
}

.about-stats > div {
  text-align: center;
  min-width: 0;
}

.about-stats strong {
  display: block;
  font-size: 1.75rem;
  line-height: 1.1;
  color: var(--bs-primary);
  text-shadow: 0 0 18px rgba(var(--bs-primary-rgb), 0.35);
}

.about-stats span {
  display: block;
  margin-top: 0.4rem;
  font-size: 0.8rem;
  line-height: 1.3;
}

/* Tablet */
@media (max-width: 991.98px) {
  .about-stats {
    grid-template-columns: repeat(4, 1fr);
  }
}

/* Mobile */
@media (max-width: 575.98px) {
  .about-stats {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* ==========================================================
   BOOKING
========================================================== */

.booking-card {
  padding: clamp(1.5rem, 4vw, 3rem);

  border: 1px solid var(--dj-border);
  border-radius: 1rem;

  background: linear-gradient(
    145deg,
    rgba(255, 255, 255, 0.06),
    rgba(255, 255, 255, 0.015)
  );

  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.35);
}

.booking-heading {
  margin-bottom: 2rem;
}

.booking-heading h3 {
  color: #fff;

  font-size: clamp(2rem, 4vw, 3.5rem);
  font-weight: 800;
  line-height: 1;

  text-transform: uppercase;
}

.booking-heading h3 span {
  display: block;
  color: var(--bs-primary);
}

.booking-heading p {
  margin-top: 1rem;
  color: var(--dj-muted);
}

.booking-card .form-control {
  border-color: rgba(255, 255, 255, 0.12);
  background-color: rgba(255, 255, 255, 0.06);
  color: #fff;
}

.booking-card .form-control.is-invalid {
  border-color: var(--bs-form-invalid-border-color);
}

.booking-card .form-control.is-invalid:focus {
  border-color: var(--bs-form-invalid-border-color);
  box-shadow: 0 0 0 0.25rem rgba(var(--bs-danger-rgb), 0.25);
}

.booking-card .form-control:focus {
  border-color: var(--bs-primary);

  background-color: rgba(255, 255, 255, 0.08);
  color: #fff;

  box-shadow: 0 0 0 0.25rem rgba(var(--bs-primary-rgb), 0.15);
}

.booking-card .form-floating > label {
  color: rgba(255, 255, 255, 0.6);
}

.booking-card textarea.form-control {
  min-height: 160px;
}

/* ==========================================================
   RESPONSIVE
========================================================== */

@media (max-width: 991.98px) {
  .hero-section {
    min-height: auto;
    padding-top: 4rem !important;
    padding: 0.51rem 0 5rem;
  }

  .twitch-cta,
  .stream-section,
  .mixes-section,
  .about-section {
    padding: 5rem 0;
  }
}

@media (max-width: 575.98px) {
  .hero-section {
    min-height: 760px;
    padding-top: 2rem !important;
  }

  .hero-title {
    font-size: clamp(4.5rem, 24vw, 7rem);
  }

  .hero-actions {
    flex-direction: column;
  }

  .hero-actions .btn {
    width: 100%;
  }

  .display-title {
    font-size: 2.75rem;
  }

  .twitch-features {
    flex-direction: column;
    gap: 0.75rem;
  }

  .stream-topbar {
    align-items: flex-start;
    flex-direction: column;
    gap: 0.5rem;
  }

  #twitch-embed {
    min-height: 300px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .twitch-glow,
  .twitch-beam,
  .live-dot,
  .twitch-button-glow {
    animation: none !important;
  }

  .twitch-background-text {
    transition: none;
  }
}

.twitch-schedule {
  display: inline-flex;
  flex-direction: column;
  align-items: center;

  margin: 1.75rem auto 0;
  padding: 1rem 2rem;

  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 0.75rem;

  background: rgba(255, 255, 255, 0.04);
  backdrop-filter: blur(10px);
}

.twitch-schedule-label {
  margin-bottom: 0.35rem;

  color: rgba(255, 255, 255, 0.65);

  font-size: 0.75rem;
  font-weight: 700;

  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.twitch-schedule-time {
  color: #fff;

  font-size: 1.25rem;
  font-weight: 700;
}

.twitch-schedule-zone {
  margin-top: 0.3rem;

  color: rgba(255, 255, 255, 0.45);

  font-size: 0.75rem;
}

@media (max-width: 575.98px) {
  .twitch-schedule {
    width: 100%;
    padding: 1rem;
  }

  .twitch-schedule-time {
    font-size: 1.1rem;
  }
}
</style>
